/**
 * BLRRD Library Modal — Files + Projects browser.
 *
 * Phase 2.D file-basics UI. Uses a native <dialog> element (showModal) for
 * accessibility wins out of the box: focus trap, Esc-close, ::backdrop dim,
 * inert page underneath.
 *
 * The DOM is rendered LAZILY on first openLibraryModal() call so the static
 * index.html stays lean. Cached on `state.dialog` for subsequent opens.
 *
 * Hardening notes (per Codex pre-flight):
 *   - Project rendering does ONE listFiles() pass to build a Map<hash, meta>
 *     and joins against listProjects() — never calls getFile() per-card
 *     (that would load every blob into memory).
 *   - Object URLs for thumbnail images are tracked in a Set and revoked on
 *     re-render and on dialog close. No leaks across opens.
 *   - Wipe sequences flushAutosave: cancelPendingAutosave() →
 *     waitForAutosaveIdle() → clearFiles() → clearCurrentImage(). Without
 *     this, an in-flight save could resurrect a row right after wipe.
 *   - Delete-current-file follows the same drain sequence so autosave can't
 *     re-create the just-deleted row from a still-pending snap.
 *   - Project open uses restoreEditorState({pushUndo:false, toast:false}),
 *     NOT applySnapshot — applySnapshot pushes undo + emits a toast like a
 *     manual snapshot click, which is wrong for a "load my work" path.
 *   - All DOM construction uses createElement/textContent/appendChild. No
 *     innerHTML — security-lint enforces this.
 */

import { t, formatNumber } from '../i18n/index.js';

const state = {
    dialog: null,
    tabsEl: null,
    bodyEl: null,
    activeTab: 'files',
    objectURLs: new Set(),
    initialized: false,
};

/* ── helpers ───────────────────────────────────────────────────────── */

function makeEl(tag, attrs = {}, ...children) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (v == null || v === false) continue;
        if (k === 'className') el.className = v;
        else if (k === 'dataset') Object.assign(el.dataset, v);
        else if (k === 'i18n') el.dataset.i18n = v;
        else if (k.startsWith('on') && typeof v === 'function') {
            el.addEventListener(k.slice(2).toLowerCase(), v);
        } else if (k === 'aria') {
            for (const [ak, av] of Object.entries(v)) el.setAttribute(`aria-${ak}`, av);
        } else if (k in el && typeof el[k] !== 'object') {
            el[k] = v;
        } else {
            el.setAttribute(k, v);
        }
    }
    for (const c of children) {
        if (c == null || c === false) continue;
        if (typeof c === 'string') el.appendChild(document.createTextNode(c));
        else el.appendChild(c);
    }
    return el;
}

function revokeAllObjectURLs() {
    for (const url of state.objectURLs) {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    }
    state.objectURLs.clear();
}

function thumbnailURL(blob) {
    if (!blob) return null;
    try {
        const url = URL.createObjectURL(blob);
        state.objectURLs.add(url);
        return url;
    } catch {
        return null;
    }
}

function formatBytes(n) {
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(ts) {
    if (!Number.isFinite(ts)) return '';
    try {
        return new Date(ts).toLocaleString();
    } catch {
        return '';
    }
}

/* ── DOM construction ──────────────────────────────────────────────── */

function buildDialog() {
    const closeBtn = makeEl('button', {
        className: 'library-dialog-close',
        type: 'button',
        'aria-label': t('library.close'),
        onClick: closeDialog,
    }, '✕');

    const titleEl = makeEl('h3', { id: 'libraryTitle' }, t('library.title'));

    const head = makeEl('div', { className: 'library-dialog-head' },
        titleEl,
        makeEl('div', { className: 'library-spacer' }),
        closeBtn,
    );

    const tabFiles = makeEl('button', {
        className: 'library-tab',
        type: 'button',
        role: 'tab',
        'aria-selected': 'true',
        onClick: () => setActiveTab('files'),
    }, t('library.filesTab'));
    tabFiles.dataset.tab = 'files';

    const tabProjects = makeEl('button', {
        className: 'library-tab',
        type: 'button',
        role: 'tab',
        'aria-selected': 'false',
        onClick: () => setActiveTab('projects'),
    }, t('library.projectsTab'));
    tabProjects.dataset.tab = 'projects';

    const tabs = makeEl('div', { className: 'library-tabs', role: 'tablist' },
        tabFiles, tabProjects);

    const body = makeEl('div', { className: 'library-body', role: 'tabpanel' });

    const wipeBtn = makeEl('button', {
        className: 'library-wipe-btn',
        type: 'button',
        onClick: handleWipe,
    }, t('library.wipe'));

    const foot = makeEl('div', { className: 'library-foot' },
        makeEl('div', { className: 'library-spacer' }),
        wipeBtn,
    );

    const dialog = makeEl('dialog', {
        className: 'library-dialog',
        'aria-labelledby': 'libraryTitle',
    }, head, tabs, body, foot);

    /* Close on backdrop click — match macOS/iOS sheet semantics. */
    dialog.addEventListener('click', (e) => {
        if (e.target === dialog) closeDialog();
    });

    /* Cleanup on close (Esc or ×). */
    dialog.addEventListener('close', () => revokeAllObjectURLs());

    document.body.appendChild(dialog);

    state.dialog = dialog;
    state.tabsEl = tabs;
    state.bodyEl = body;
    state.initialized = true;
}

function setActiveTab(tab) {
    if (state.activeTab === tab) return;
    state.activeTab = tab;
    state.tabsEl.querySelectorAll('.library-tab').forEach((t2) => {
        const isActive = t2.dataset.tab === tab;
        t2.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    renderActiveTab();
}

/* ── tab rendering ─────────────────────────────────────────────────── */

async function renderActiveTab() {
    revokeAllObjectURLs();
    state.bodyEl.replaceChildren();
    const placeholder = makeEl('div', { className: 'library-empty-msg' }, '…');
    state.bodyEl.appendChild(placeholder);

    if (state.activeTab === 'files') {
        await renderFilesTab();
    } else {
        await renderProjectsTab();
    }
}

async function renderFilesTab() {
    let files = [];
    try {
        files = await window.BlrrdFiles.listFiles();
    } catch {
        files = [];
    }

    state.bodyEl.replaceChildren();
    if (!files || files.length === 0) {
        state.bodyEl.appendChild(makeEl('div', { className: 'library-empty-msg' },
            t('library.empty')));
        return;
    }

    const grid = makeEl('div', { className: 'library-grid' });
    for (const f of files) grid.appendChild(buildFileCard(f));
    state.bodyEl.appendChild(grid);
}

async function renderProjectsTab() {
    let projects = [];
    let files = [];
    try {
        [projects, files] = await Promise.all([
            window.BlrrdProjects.listProjects(),
            window.BlrrdFiles.listFiles(),
        ]);
    } catch {
        projects = [];
        files = [];
    }

    /* ONE pass to build a Map<fileHash, fileMeta>. Avoids per-card getFile()
     * (which would load every original blob into memory just to render the
     * project list). */
    const fileByHash = new Map();
    for (const f of files) fileByHash.set(f.hash, f);

    state.bodyEl.replaceChildren();
    if (!projects || projects.length === 0) {
        state.bodyEl.appendChild(makeEl('div', { className: 'library-empty-msg' },
            t('library.empty')));
        return;
    }

    const grid = makeEl('div', { className: 'library-grid' });
    for (const p of projects) {
        grid.appendChild(buildProjectCard(p, fileByHash.get(p.fileHash) || null));
    }
    state.bodyEl.appendChild(grid);
}

function buildFileCard(file) {
    const url = thumbnailURL(file.thumbnail);
    const thumb = makeEl('div', {
        className: 'library-card-thumb',
        title: t('library.open'),
        onClick: () => openFile(file),
    });
    if (url) {
        thumb.appendChild(makeEl('img', { src: url, alt: file.name || '' }));
    } else {
        thumb.appendChild(makeEl('div', { className: 'library-card-thumb-empty' }, '🖼'));
    }

    const subBits = [];
    if (file.width && file.height) subBits.push(`${file.width}×${file.height}`);
    if (file.size) subBits.push(formatBytes(file.size));

    const meta = makeEl('div', { className: 'library-card-meta' },
        makeEl('div', { className: 'library-card-name', title: file.name || '' },
            file.name || t('library.noFile')),
        makeEl('div', { className: 'library-card-sub' },
            subBits.join(' · ') || formatDate(file.createdAt)),
    );

    const openBtn = makeEl('button', {
        className: 'btn-open',
        type: 'button',
        onClick: () => openFile(file),
    }, t('library.open'));

    const delBtn = makeEl('button', {
        className: 'btn-del',
        type: 'button',
        onClick: () => deleteFile(file),
    }, t('library.delete'));

    const actions = makeEl('div', { className: 'library-card-actions' }, openBtn, delBtn);

    return makeEl('div', { className: 'library-card' }, thumb, meta, actions);
}

function buildProjectCard(project, fileMeta) {
    const url = thumbnailURL(project.thumbnail);
    const fileName = fileMeta?.name || (project.fileHash ? project.name : t('library.noFile'));

    const thumb = makeEl('div', {
        className: 'library-card-thumb',
        title: t('library.open'),
        onClick: () => openProject(project, fileMeta),
    });
    if (url) {
        thumb.appendChild(makeEl('img', { src: url, alt: project.name || '' }));
    } else {
        thumb.appendChild(makeEl('div', { className: 'library-card-thumb-empty' }, '🎨'));
    }

    const meta = makeEl('div', { className: 'library-card-meta' },
        makeEl('div', { className: 'library-card-name', title: fileName },
            fileName || t('library.newProject')),
        makeEl('div', { className: 'library-card-sub' },
            `${t('library.lastEdited')}: ${formatDate(project.updatedAt)}`),
    );

    const openBtn = makeEl('button', {
        className: 'btn-open',
        type: 'button',
        disabled: !fileMeta,
        onClick: () => openProject(project, fileMeta),
    }, t('library.open'));

    const delBtn = makeEl('button', {
        className: 'btn-del',
        type: 'button',
        onClick: () => deleteProject(project),
    }, t('library.delete'));

    const actions = makeEl('div', { className: 'library-card-actions' }, openBtn, delBtn);

    return makeEl('div', { className: 'library-card' }, thumb, meta, actions);
}

/* ── actions ───────────────────────────────────────────────────────── */

async function openFile(file) {
    if (!file || !file.hash) return;
    try {
        const full = await window.BlrrdFiles.getFile(file.hash);
        if (!full || !full.blob) return;
        if (typeof window.loadImage === 'function') {
            window.loadImage(full.blob);
            closeDialog();
        }
    } catch {
        /* swallow — IDB read failures must not crash UI */
    }
}

async function deleteFile(file) {
    if (!file || !file.hash) return;
    const msg = t('library.deleteConfirm', { name: file.name || file.hash });
    if (!window.confirm(msg)) return;

    /* If we're deleting the file currently being edited, drain autosave
     * BEFORE deleting + immediately clear editor state, so a still-pending
     * commit can't recreate the row right after we delete it.
     *
     * Codex FB-UI review fix: waitForAutosaveIdle returns FALSE on the 5s
     * timeout path. Proceeding to deleteFile after a non-idle wait races
     * an in-flight tx that may commit AFTER our delete and re-create the
     * row. ABORT instead of proceeding — the user can retry, or close the
     * editor first to drain the save naturally. */
    const isCurrent = window.E && window.E.imgKey === file.hash;
    if (isCurrent && typeof window.cancelPendingAutosave === 'function') {
        window.cancelPendingAutosave();
        if (typeof window.waitForAutosaveIdle === 'function') {
            let idle = false;
            try { idle = await window.waitForAutosaveIdle(); } catch { idle = false; }
            if (!idle) {
                window.alert(t('library.busy') || 'Editor is still saving — try again in a moment.');
                return;
            }
        }
    }

    try {
        await window.BlrrdFiles.deleteFile(file.hash);
    } catch { /* ignore */ }

    if (isCurrent && typeof window.clearCurrentImage === 'function') {
        window.clearCurrentImage({ silent: true });
    }
    await renderActiveTab();
}

async function openProject(project, fileMeta) {
    if (!project) return;
    if (!fileMeta) return;
    try {
        const full = await window.BlrrdFiles.getFile(project.fileHash);
        if (!full || !full.blob) return;
        if (typeof window.loadImage !== 'function') return;
        await window.loadImage(full.blob);
        if (typeof window.restoreEditorState === 'function') {
            window.restoreEditorState(project.state || {}, { pushUndo: false, toast: false });
        }
        closeDialog();
    } catch {
        /* swallow */
    }
}

async function deleteProject(project) {
    if (!project || !project.id) return;
    const msg = t('library.deleteConfirm', { name: project.name || project.id });
    if (!window.confirm(msg)) return;
    try {
        await window.BlrrdProjects.deleteProject(project.id);
    } catch { /* ignore */ }
    await renderActiveTab();
}

async function handleWipe() {
    if (!window.confirm(t('library.wipeConfirm'))) return;

    /* Drain autosave first — without this a commit in flight (or scheduled
     * during the await) re-creates a row inside the just-cleared store.
     *
     * Codex FB-UI review fix: if waitForAutosaveIdle TIMES OUT (returns
     * false), DO NOT proceed to clearFiles. Wipe is irreversible and a
     * still-in-flight tx that lands after the clear would silently put
     * orphaned rows back. Tell the user and abort — they can retry. */
    if (typeof window.cancelPendingAutosave === 'function') {
        window.cancelPendingAutosave();
    }
    if (typeof window.waitForAutosaveIdle === 'function') {
        let idle = false;
        try { idle = await window.waitForAutosaveIdle(); } catch { idle = false; }
        if (!idle) {
            window.alert(t('library.busy') || 'Editor is still saving — wipe cancelled. Try again in a moment.');
            return;
        }
    }

    try {
        await window.BlrrdFiles.clearFiles();
    } catch { /* ignore — clearProjects is part of the same atomic tx */ }

    /* Whatever the user had open is now disconnected from any file row.
     * Reset the editor so subsequent edits don't re-persist orphaned state. */
    if (window.E && window.E.imgKey && typeof window.clearCurrentImage === 'function') {
        window.clearCurrentImage({ silent: true });
    }

    await renderActiveTab();
}

/* ── public API ────────────────────────────────────────────────────── */

function closeDialog() {
    if (state.dialog && state.dialog.open) state.dialog.close();
    revokeAllObjectURLs();
}

export function openLibraryModal() {
    if (!state.initialized) buildDialog();
    /* Re-translate the static labels every open in case locale changed. */
    refreshStaticLabels();
    /* Default to the Files tab on every open. */
    if (state.activeTab !== 'files') {
        state.activeTab = 'files';
        state.tabsEl.querySelectorAll('.library-tab').forEach((t2) => {
            t2.setAttribute('aria-selected', t2.dataset.tab === 'files' ? 'true' : 'false');
        });
    }
    state.dialog.showModal();
    /* Render after showModal so initial paint isn't blocked. */
    renderActiveTab();
}

function refreshStaticLabels() {
    if (!state.dialog) return;
    const titleEl = state.dialog.querySelector('#libraryTitle');
    if (titleEl) titleEl.textContent = t('library.title');
    const closeBtn = state.dialog.querySelector('.library-dialog-close');
    if (closeBtn) closeBtn.setAttribute('aria-label', t('library.close'));
    const tabs = state.dialog.querySelectorAll('.library-tab');
    tabs.forEach((tab) => {
        if (tab.dataset.tab === 'files') tab.textContent = t('library.filesTab');
        else if (tab.dataset.tab === 'projects') tab.textContent = t('library.projectsTab');
    });
    const wipe = state.dialog.querySelector('.library-wipe-btn');
    if (wipe) wipe.textContent = t('library.wipe');
}

/* Locale-format helpers re-export to keep tree-shake friendly. */
export { formatNumber };
